import { Link } from "react-router-dom";
import { Clock, MapPin, Users, Phone } from "lucide-react";
import { Button } from "./ui/button";

export default function BusinessCard({ c, distanceKm }) {
  return (
    <div className="group flex flex-col border border-border bg-white transition-colors hover:border-ink" data-testid={`business-card-${c.id}`}>
      {c.image && (
        <div className="aspect-[16/9] w-full overflow-hidden bg-surface">
          <img src={c.image} alt={c.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        </div>
      )}
      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="overline mb-1">{c.type}</p>
            <h3 className="font-display text-xl font-bold leading-tight text-ink">{c.name}</h3>
          </div>
          {/* Pastille statut :
             - 1 file unique → label simple « Ouvert » / « Fermé » (inchangé).
             - 2+ files → on précise « X / Y ouvertes » pour informer le
               visiteur du ratio de files disponibles. Si aucune n'est
               ouverte → « Fermé » classique (cas multi-files toutes fermées).
             `openQueueCount` et `totalQueueCount` peuvent être absents sur
             des payloads anciens → fallback gracieux sur `isOpen` seul. */}
          {(() => {
            const total = c.totalQueueCount ?? 0;
            const open = c.openQueueCount ?? 0;
            const multiQueue = total > 1;
            const isOpen = !!c.isOpen;
            const label = multiQueue && isOpen
              ? `${open} / ${total} ouvertes`
              : isOpen
                ? "Ouvert"
                : "Fermé";
            return (
              <span
                className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-bold uppercase tracking-wider ${
                  isOpen ? "bg-[#ECFBEF] text-[#0A7F2E]" : "bg-surface text-subtle"
                }`}
                data-testid={`status-${c.id}`}
                title={multiQueue ? `${open} file${open > 1 ? "s" : ""} ouverte${open > 1 ? "s" : ""} sur ${total}` : undefined}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${isOpen ? "bg-[#34C759]" : "bg-subtle"}`}></span>
                {label}
              </span>
            );
          })()}
        </div>
        <div className="mt-3 flex items-center gap-1.5 text-sm text-subtle">
          <MapPin size={14} /> {c.address}, {c.postalCode} {c.city}
          {distanceKm != null && <span className="ml-auto font-mono text-xs">{distanceKm.toFixed(1)} km</span>}
        </div>
        {/* Spec utilisateur fork 25.9 — affichage du numéro public de
           l'établissement quand il est renseigné. Cliquable (tel:) pour
           permettre à un visiteur mobile d'appeler directement avant de
           se déplacer. */}
        {c.phone && (
          <a
            href={`tel:${c.phone.replace(/\s+/g, "")}`}
            className="mt-1.5 inline-flex items-center gap-1.5 text-sm text-subtle hover:text-brand transition-colors"
            data-testid={`business-card-phone-${c.id}`}
            onClick={(e) => e.stopPropagation()}
          >
            <Phone size={14} /> {c.phone}
          </a>
        )}
        <div className="mt-4 grid grid-cols-2 gap-3 border-t border-border pt-4">
          <div>
            <p className="overline">Attente</p>
            <p className="font-mono text-2xl font-bold text-ink">
              {c.estimatedWait}<span className="text-sm font-normal text-subtle"> min</span>
            </p>
          </div>
          <div>
            <p className="overline">File</p>
            <p className="font-mono text-2xl font-bold text-ink flex items-center gap-1.5">
              <Users size={16} className="text-subtle" /> {c.waitingCount}
            </p>
          </div>
        </div>
        <Button asChild className="mt-5 bg-ink hover:bg-brand text-white rounded-md h-11 font-medium" data-testid={`view-commerce-${c.id}`}>
          <Link to={`/commerce/${c.id}`}>
            <Clock size={16} className="mr-2" /> Prendre un ticket
          </Link>
        </Button>
      </div>
    </div>
  );
}
